import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ApiError,
  api,
  type RunPermissionScope,
  type RunPermissions,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { InfoHint } from '../components/InfoHint';
import { ToggleSwitch } from '../components/ToggleSwitch';
import { composePromptForSave, splitEditablePrompt } from '../lib/llmPrompt';

type AdminSettings = {
  thresholds: {
    new_winner_min_revenue: number;
    growth_rate: number;
    min_prev_revenue: number;
  };
  llm_enabled: boolean;
  llm?: LlmSettings;
  bhapi_top_n: number;
  runs: {
    max_concurrent: number;
    concurrency_scope: 'global' | 'per_marketplace';
    stale_minutes: number;
  };
  run_permissions: RunPermissions;
};

const DEFAULT_RUN_PERMISSIONS: RunPermissions = {
  user: { retry: 'all', delete: 'own', edit: 'all', cancel: 'none' },
  viewer: { retry: 'none', delete: 'none', edit: 'none', cancel: 'none' },
};

const VIEWER_RUN_PERMISSIONS_READONLY = DEFAULT_RUN_PERMISSIONS.viewer;

const RUN_PERMISSION_LABELS: Record<RunPermissionScope, string> = {
  none: 'Запрещено',
  own: 'Свои',
  all: 'Все',
};

const RUN_PERMISSION_ACTIONS = [
  ['retry', 'Перезапуск'],
  ['delete', 'Удаление'],
  ['edit', 'Редактирование'],
  ['cancel', 'Отмена'],
] as const;

type RunPermissionAction = (typeof RUN_PERMISSION_ACTIONS)[number][0];

type LlmStageKey =
  | 'event_explanation'
  | 'winner_patterns'
  | 'hyper_growth_patterns'
  | 'hypotheses'
  | 'recommendations';

type LlmStageSettings = {
  label: string;
  model: string;
  temperature: number;
  max_tokens: number;
  prompt: string;
  prompt_version: string;
  prompt_hash: string;
};

type LlmSettings = {
  enabled: boolean;
  stages: Record<LlmStageKey, LlmStageSettings>;
};

const LLM_STAGE_TABS: Array<{ key: LlmStageKey; label: string }> = [
  { key: 'event_explanation', label: 'Комментарий к событию' },
  { key: 'winner_patterns', label: 'Паттерны NEW-WINNER' },
  { key: 'hyper_growth_patterns', label: 'Паттерны OLD-CARD' },
  { key: 'hypotheses', label: 'Гипотезы' },
  { key: 'recommendations', label: 'Рекомендации' },
];

function defaultLlmStage(label: string): LlmStageSettings {
  return {
    label,
    model: '',
    temperature: 0.2,
    max_tokens: 1000,
    prompt: '',
    prompt_version: '',
    prompt_hash: '',
  };
}

function defaultLlmSettings(enabled = true): LlmSettings {
  return {
    enabled,
    stages: Object.fromEntries(
      LLM_STAGE_TABS.map((stage) => [
        stage.key,
        defaultLlmStage(stage.label),
      ]),
    ) as Record<LlmStageKey, LlmStageSettings>,
  };
}

function normalizeLlmSettings(
  settings: LlmSettings | undefined,
  enabledFallback: boolean,
): LlmSettings {
  const fallback = defaultLlmSettings(enabledFallback);
  if (!settings) return fallback;

  return {
    enabled: Boolean(settings.enabled),
    stages: Object.fromEntries(
      LLM_STAGE_TABS.map((stage) => [
        stage.key,
        {
          ...fallback.stages[stage.key],
          ...settings.stages?.[stage.key],
          label: stage.label,
        },
      ]),
    ) as Record<LlmStageKey, LlmStageSettings>,
  };
}

function normalizeRunPermissions(
  permissions: RunPermissions | undefined,
): RunPermissions {
  return {
    user: {
      ...DEFAULT_RUN_PERMISSIONS.user,
      ...(permissions?.user ?? {}),
    },
    viewer: VIEWER_RUN_PERMISSIONS_READONLY,
  };
}

function numberInputValue(value: number): number | '' {
  return Number.isFinite(value) ? value : '';
}

export function AdminSettingsPage() {
  const { user } = useAuth();
  if (user && user.role !== 'admin') return <Navigate to="/" replace />;

  return <AdminSettingsForm />;
}

function AdminSettingsForm() {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [llmSaving, setLlmSaving] = useState(false);

  const [thresholds, setThresholds] = useState({
    new_winner_min_revenue: 1_000_000,
    growth_rate: 1,
    min_prev_revenue: 100_000,
  });
  const [llmEnabled, setLlmEnabled] = useState(true);
  const [bhapiTopN, setBhapiTopN] = useState(20);
  const [runsMaxConcurrent, setRunsMaxConcurrent] = useState(1);
  const [runsScope, setRunsScope] = useState<'global' | 'per_marketplace'>(
    'global',
  );
  const [runsStaleMinutes, setRunsStaleMinutes] = useState(45);
  const [runPermissions, setRunPermissions] = useState<RunPermissions>(
    DEFAULT_RUN_PERMISSIONS,
  );
  const [llm, setLlm] = useState<LlmSettings>(() => defaultLlmSettings());
  const [selectedLlmStage, setSelectedLlmStage] =
    useState<LlmStageKey>('event_explanation');

  useEffect(() => {
    api
      .get<AdminSettings>('/api/admin/settings')
      .then((s) => {
        setThresholds(s.thresholds);
        setLlmEnabled(Boolean(s.llm_enabled));
        setBhapiTopN(s.bhapi_top_n ?? 20);
        setRunsMaxConcurrent(s.runs?.max_concurrent ?? 1);
        setRunsScope(s.runs?.concurrency_scope ?? 'global');
        setRunsStaleMinutes(s.runs?.stale_minutes ?? 45);
        setRunPermissions(normalizeRunPermissions(s.run_permissions));
        setLlm(normalizeLlmSettings(s.llm, Boolean(s.llm_enabled)));
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  async function saveAll(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    setError(null);
    try {
      await api.put('/api/admin/settings/thresholds', thresholds);
      await api.put('/api/admin/settings/llm-bhapi', {
        llm_enabled: llmEnabled,
        bhapi_top_n: bhapiTopN,
      });
      await api.put('/api/admin/settings/runs', {
        max_concurrent: runsMaxConcurrent,
        concurrency_scope: runsScope,
        stale_minutes: runsStaleMinutes,
      });
      await api.put('/api/admin/settings/run-permissions', runPermissions);
      setMessage('Администрирование сохранено');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function saveLlm() {
    if (!isSelectedLlmStageValid) return;
    setMessage(null);
    setError(null);
    setLlmSaving(true);
    try {
      const normalized = await api.put<LlmSettings>(
        '/api/admin/settings/llm',
        llm,
      );
      setLlm(normalizeLlmSettings(normalized, llm.enabled));
      setMessage('LLM по этапам сохранён');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLlmSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="text-text-tertiary">Загрузка администрирования…</div>
    );
  }

  function updateRunPermission(
    role: 'user',
    action: RunPermissionAction,
    scope: RunPermissionScope,
  ) {
    setRunPermissions((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [action]: scope,
      },
    }));
  }

  function updateSelectedLlmStage(
    patch: Partial<
      Pick<LlmStageSettings, 'model' | 'temperature' | 'max_tokens' | 'prompt'>
    >,
  ) {
    setLlm((prev) => ({
      ...prev,
      stages: {
        ...prev.stages,
        [selectedLlmStage]: {
          ...prev.stages[selectedLlmStage],
          ...patch,
        },
      },
    }));
  }

  const selectedStage = llm.stages[selectedLlmStage];
  const selectedPrompt = splitEditablePrompt(selectedStage.prompt);
  const isSelectedLlmStageValid =
    selectedStage.model.trim().length > 0 &&
    selectedPrompt.editable.trim().length > 0 &&
    Number.isFinite(selectedStage.temperature) &&
    selectedStage.temperature >= 0 &&
    selectedStage.temperature <= 2 &&
    Number.isFinite(selectedStage.max_tokens) &&
    selectedStage.max_tokens > 0;

  return (
    <div className="max-w-2xl space-y-8">
      <div className="page-header">
        <h1 className="text-h1 text-text-primary">Администрирование</h1>
        <p className="text-label text-text-tertiary mt-1">
          Не меняют уже созданные прогоны и сохранённые расписания. Дефолты
          только подставляются в новые формы, а лимиты защищают выполнение.
        </p>
      </div>

      {message ? (
        <div className="pill-success inline-block">{message}</div>
      ) : null}
      {error ? <div className="error-hint">{error}</div> : null}

      <form onSubmit={saveAll} className="space-y-6">
        <section className="card card-padding space-y-5">
          <div>
            <h2 className="section-title">Дефолты новых прогонов</h2>
            <p className="hint mt-1">
              Подставляются при создании ручного прогона или расписания. После
              сохранения конкретный прогон хранит собственные параметры.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-label font-strong text-text-primary flex items-center gap-2">
              Пороги
              <InfoHint text="Стартовые значения для формы нового прогона. Пользователь может изменить их перед запуском. Раздел 3 этими порогами не фильтруется." />
            </h3>
            <p className="hint">
              Порог раздела 1 и два порога раздела 2. На сравнение брендов и продавцов в разделе 3 не влияют.
            </p>
            <label className="block">
              <span className="form-label">Раздел 1 · минимум заказов, ₽</span>
              <input
                type="number"
                className="input w-full mt-1"
                value={thresholds.new_winner_min_revenue}
                onChange={(e) =>
                  setThresholds({
                    ...thresholds,
                    new_winner_min_revenue: Number(e.target.value),
                  })
                }
              />
              <span className="hint mt-1 block">Минимальная сумма заказов новой карточки за анализируемый месяц.</span>
            </label>
            <label className="block">
              <span className="form-label">
                Раздел 2 · рост к прошлому месяцу (0.3 = 30%)
              </span>
              <input
                type="number"
                step="0.1"
                className="input w-full mt-1"
                value={thresholds.growth_rate}
                onChange={(e) =>
                  setThresholds({
                    ...thresholds,
                    growth_rate: Number(e.target.value),
                  })
                }
              />
              <span className="hint mt-1 block">Фактический рост должен быть строго выше порога.</span>
            </label>
            <label className="block">
              <span className="form-label">Раздел 2 · заказы прошлого месяца от, ₽</span>
              <input
                type="number"
                className="input w-full mt-1"
                value={thresholds.min_prev_revenue}
                onChange={(e) =>
                  setThresholds({
                    ...thresholds,
                    min_prev_revenue: Number(e.target.value),
                  })
                }
              />
              <span className="hint mt-1 block">Защита от сигнала роста с низкой базы прошлого месяца.</span>
            </label>
          </div>

          <div className="space-y-4 border-t border-border-default/[0.08] pt-4">
            <h3 className="text-label font-strong text-text-primary flex items-center gap-2">
              AI и детализация
              <InfoHint text="Стартовые значения для новых прогонов и расписаний. AI только объясняет найденные события, детализация дополняет TOP-N карточек." />
            </h3>
            <ToggleSwitch
              checked={llmEnabled}
              onChange={setLlmEnabled}
              label="AI-комментарии включены по умолчанию"
              description="Формируют пояснения и разделы 4–7, но не меняют отбор событий и раздел 3."
            />
            <label className="block">
              <span className="form-label">Карточек для детализации</span>
              <input
                type="number"
                className="input w-full mt-1 max-w-[120px]"
                value={bhapiTopN}
                onChange={(e) => setBhapiTopN(Number(e.target.value))}
              />
              <span className="hint mt-1 block">TOP-N уже отобранных карточек разделов 1–2; состав отчёта не меняется.</span>
            </label>
          </div>
        </section>

        <section className="card card-padding space-y-5">
          <div>
            <h2 className="section-title flex items-center gap-2">
              LLM по этапам
              <InfoHint text="Настройки RouterAI для отдельных этапов отчёта." />
            </h2>
          </div>

          <div className="flex flex-wrap gap-2" role="tablist">
            {LLM_STAGE_TABS.map((stage) => (
              <button
                key={stage.key}
                type="button"
                className={
                  selectedLlmStage === stage.key
                    ? 'btn-subtle btn-md'
                    : 'btn-ghost btn-md'
                }
                disabled={llmSaving}
                aria-selected={selectedLlmStage === stage.key}
                onClick={() => setSelectedLlmStage(stage.key)}
              >
                {stage.label}
              </button>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="form-label">model</span>
              <input
                type="text"
                className="input w-full mt-1"
                value={selectedStage.model}
                onChange={(e) =>
                  updateSelectedLlmStage({ model: e.target.value })
                }
              />
            </label>
            <label className="block">
              <span className="form-label">temperature</span>
              <input
                type="number"
                min={0}
                max={2}
                step="0.1"
                className="input w-full mt-1"
                value={numberInputValue(selectedStage.temperature)}
                onChange={(e) =>
                  updateSelectedLlmStage({
                    temperature:
                      e.target.value === '' ? Number.NaN : Number(e.target.value),
                  })
                }
              />
            </label>
            <label className="block">
              <span className="form-label">max_tokens</span>
              <input
                type="number"
                min={1}
                className="input w-full mt-1"
                value={numberInputValue(selectedStage.max_tokens)}
                onChange={(e) =>
                  updateSelectedLlmStage({
                    max_tokens:
                      e.target.value === '' ? Number.NaN : Number(e.target.value),
                  })
                }
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="form-label">prompt</span>
              <textarea
                className="input w-full mt-1 min-h-[220px]"
                value={selectedPrompt.editable}
                onChange={(e) =>
                  updateSelectedLlmStage({
                    prompt: composePromptForSave(
                      e.target.value,
                      selectedPrompt.lockedSuffix,
                    ),
                  })
                }
              />
            </label>
          </div>

          <div className="flex justify-end border-t border-border-default/[0.08] pt-4">
            <button
              type="button"
              className="btn-primary btn-md min-w-[140px]"
              disabled={!isSelectedLlmStageValid || llmSaving}
              onClick={saveLlm}
            >
              {llmSaving ? 'Сохранение…' : 'Сохранить LLM'}
            </button>
          </div>
        </section>

        <section className="card card-padding space-y-4">
          <div>
            <h2 className="section-title flex items-center gap-2">
              Лимиты выполнения
              <InfoHint text="Предохранители системы. Применяются при старте ручных прогонов и запусков по расписанию, а также при проверке зависших прогонов." />
            </h2>
            <p className="hint mt-1">
              Эти значения не меняют аналитику отчёта, но ограничивают нагрузку
              и помогают завершать зависшие выполнения.
            </p>
          </div>
          <label className="block">
            <span className="form-label">Макс. одновременных прогонов</span>
            <input
              type="number"
              min={0}
              max={20}
              className="input w-full mt-1 max-w-[120px]"
              value={runsMaxConcurrent}
              onChange={(e) => setRunsMaxConcurrent(Number(e.target.value))}
            />
          </label>
          <label className="block">
            <span className="form-label">Область лимита</span>
            <select
              className="input w-full mt-1 max-w-[240px]"
              value={runsScope}
              onChange={(e) =>
                setRunsScope(e.target.value as 'global' | 'per_marketplace')
              }
            >
              <option value="global">Вся система</option>
              <option value="per_marketplace">На маркетплейс</option>
            </select>
          </label>
          <label className="block">
            <span className="form-label">Таймаут зависшего прогона (мин)</span>
            <input
              type="number"
              min={1}
              className="input w-full mt-1 max-w-[120px]"
              value={runsStaleMinutes}
              onChange={(e) => setRunsStaleMinutes(Number(e.target.value))}
            />
          </label>
        </section>

        <section className="card card-padding space-y-4">
          <div>
            <h2 className="section-title flex items-center gap-2">
              Права на прогоны
              <InfoHint text="Правила применяются на backend для фактических прогонов. Admin всегда сохраняет полный доступ." />
            </h2>
          </div>
          <div className="grid gap-4">
            <div className="rounded-md bg-surface-2 p-3">
              <div className="rubric mb-3">user</div>
              <div className="grid gap-3 sm:grid-cols-2">
                {RUN_PERMISSION_ACTIONS.map(([action, label]) => (
                  <label key={action} className="block">
                    <span className="form-label">{label}</span>
                    <select
                      className="input w-full mt-1"
                      value={runPermissions.user[action]}
                      onChange={(e) =>
                        updateRunPermission(
                          'user',
                          action,
                          e.target.value as RunPermissionScope,
                        )
                      }
                    >
                      {(['none', 'own', 'all'] as const).map((scope) => (
                        <option key={scope} value={scope}>
                          {RUN_PERMISSION_LABELS[scope]}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            </div>
            <div className="rounded-md bg-surface-2 p-3">
              <div className="rubric mb-3">viewer</div>
              <p className="hint">
                Роль viewer доступна только для просмотра прогонов, выгрузки отчётов и
                собственных настроек профиля. Запуск, перезапуск, редактирование,
                отмена и удаление прогонов всегда запрещены.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {RUN_PERMISSION_ACTIONS.map(([, label]) => (
                  <div
                    key={label}
                    className="flex items-center justify-between gap-3 rounded-sm bg-surface px-3 py-2"
                  >
                    <span className="text-label text-text-secondary">{label}</span>
                    <span className="pill-neutral">Запрещено</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="flex flex-col gap-3 border-t border-border-default/[0.08] pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="hint max-w-lg">
            Дефолты будут подставляться только в новые прогоны и расписания.
            Лимиты начнут действовать при следующих стартах и проверках зависших
            прогонов.
          </p>
          <button type="submit" className="btn-primary btn-md min-w-[140px]">
            Сохранить
          </button>
        </div>
      </form>
    </div>
  );
}
